import { useEffect, useState } from 'preact/hooks';
import { ApiError, createApi, type Api } from '../api.js';
import { saveSession, sessionFrom, takeReturnTo } from '../auth.js';
import { LangContext, texts, useLang, type Lang } from '../i18n.js';

/**
 * Sign-in with email and password (owner 2026-10-28: everyone, freelancers on @gmail too). The username is the email
 * address. After the password comes two-step verification when it is set up or required: the code from an
 * authenticator app, or setting one up there and then. The Worker talks to Supabase (services/passwordAuth.ts).
 */
const TX = texts(
  {
    or: 'or',
    email: 'Email',
    password: 'Password',
    show: 'Show',
    hide: 'Hide',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    signInPw: 'Sign in with password',
    forgot: 'Forgot password?',
    forgotTitle: 'Choose a new password',
    forgotBody: "Enter your email and we'll send you a link to choose a new password.",
    send: 'Send link',
    sent: (email: string) => `If ${email} is in KFF Hub, a link is on its way. Check your inbox (and spam) – it works for one hour.`,
    back: 'Back to sign-in',
    challengeTitle: 'Two-step verification',
    challengeBody: 'Enter the six-digit code from your authenticator app.',
    code: 'Code',
    confirm: 'Confirm',
    enrollTitle: 'Turn on two-step verification',
    enrollBody: 'Your account needs a code from an authenticator app as well as the password.',
    enroll1: 'Install an authenticator app on your phone, e.g. Google Authenticator, Microsoft Authenticator or 1Password.',
    enroll2: 'Scan the QR code with the app.',
    enroll2b: "Can't scan? Enter this key in the app:",
    enroll3: 'Enter the six-digit code the app shows.',
    qr: 'QR code for the authenticator app',
    setTitle: (name: string) => `Hi ${name}, choose your password`,
    resetTitle: 'Choose a new password',
    username: 'Your username is your email address:',
    newPassword: 'New password',
    repeat: 'Repeat the password',
    rules: 'At least 12 characters. A few words in a row is easy to remember and hard to guess.',
    mismatch: "The passwords aren't the same.",
    save: 'Save password',
    linkGone: 'This link has expired or was already used.',
    newLink: 'Ask for a new link',
    loading: 'Loading…'
  },
  {
    or: 'eller',
    email: 'E-post',
    password: 'Lösenord',
    show: 'Visa',
    hide: 'Dölj',
    signIn: 'Logga in',
    signingIn: 'Loggar in…',
    signInPw: 'Logga in med lösenord',
    forgot: 'Glömt lösenordet?',
    forgotTitle: 'Välj ett nytt lösenord',
    forgotBody: 'Fyll i din e-post så skickar vi en länk där du väljer ett nytt lösenord.',
    send: 'Skicka länk',
    sent: (email: string) => `Om ${email} finns i KFF Hub är en länk på väg. Titta i inkorgen (och skräpposten) – den gäller i en timme.`,
    back: 'Tillbaka till inloggningen',
    challengeTitle: 'Tvåstegsverifiering',
    challengeBody: 'Skriv in den sexsiffriga koden från din autentiseringsapp.',
    code: 'Kod',
    confirm: 'Bekräfta',
    enrollTitle: 'Slå på tvåstegsverifiering',
    enrollBody: 'Ditt konto behöver en kod från en autentiseringsapp utöver lösenordet.',
    enroll1: 'Installera en autentiseringsapp i mobilen, t.ex. Google Authenticator, Microsoft Authenticator eller 1Password.',
    enroll2: 'Skanna QR-koden med appen.',
    enroll2b: 'Går det inte att skanna? Skriv in den här nyckeln i appen:',
    enroll3: 'Skriv in den sexsiffriga koden som appen visar.',
    qr: 'QR-kod för autentiseringsappen',
    setTitle: (name: string) => `Hej ${name}, välj ditt lösenord`,
    resetTitle: 'Välj ett nytt lösenord',
    username: 'Ditt användarnamn är din e-postadress:',
    newPassword: 'Nytt lösenord',
    repeat: 'Upprepa lösenordet',
    rules: 'Minst 12 tecken. Några ord i rad är lätt att komma ihåg och svårt att gissa.',
    mismatch: 'Lösenorden är inte likadana.',
    save: 'Spara lösenord',
    linkGone: 'Länken har gått ut eller redan använts.',
    newLink: 'Be om en ny länk',
    loading: 'Laddar…'
  }
);

type WireSession = { accessToken: string; refreshToken: string; expiresIn: number };
type SignInResult = { session: WireSession } & ({ mfa: 'none' } | { mfa: 'challenge'; factorId: string } | { mfa: 'enroll' });

const anon = () => createApi(null);
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Signed in: the session is kept on the device and the Hub opens where the person was going. */
function finish(s: WireSession) {
  saveSession(sessionFrom(s));
  location.assign(takeReturnTo());
}

function Err(p: { text: string }) {
  return p.text ? (
    <p class="login-error" role="alert">
      {p.text}
    </p>
  ) : null;
}

function PasswordInput(p: { id: string; label: string; value: string; onInput: (v: string) => void; autocomplete: string; describedBy?: string }) {
  const t = TX[useLang()];
  const [shown, setShown] = useState(false);
  return (
    <div class="login-field">
      <label class="label" for={p.id}>
        {p.label}
      </label>
      <div class="login-pw">
        <input id={p.id} class="input input--wide" type={shown ? 'text' : 'password'} value={p.value} autocomplete={p.autocomplete} required aria-describedby={p.describedBy} onInput={(e) => p.onInput((e.target as HTMLInputElement).value)} />
        <button type="button" class="btn btn-sm login-pw-toggle" aria-pressed={shown} onClick={() => setShown(!shown)}>
          {shown ? t.hide : t.show}
        </button>
      </div>
    </div>
  );
}

/** The code from the app, or setting the app up first. `token` is the session from the password (before two-step). */
export function MfaStep(p: { token: string; mode: 'challenge' | 'enroll'; factorId?: string; onDone: (s: WireSession) => void; onCancel?: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const api: Api = createApi({ kind: 'staff', token: p.token });
  const [factor, setFactor] = useState(p.factorId ?? '');
  const [setup, setSetup] = useState<{ qr: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (p.mode !== 'enroll') return;
    api.post<{ factorId: string; qr: string; secret: string }>('/api/auth/mfa/enroll', {}).then(
      (r) => {
        setFactor(r.factorId);
        setSetup({ qr: r.qr, secret: r.secret });
      },
      (e) => setErr(errText(e))
    );
  }, [p.token, p.mode]);
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      p.onDone(await api.post<WireSession>('/api/auth/mfa/verify', { factorId: factor, code, lang }));
    } catch (x) {
      setErr(errText(x));
      setBusy(false);
    }
  };
  return (
    <form class="login-form" onSubmit={(e) => void submit(e)}>
      <h2 class="login-step">{p.mode === 'enroll' ? t.enrollTitle : t.challengeTitle}</h2>
      {p.mode === 'enroll' ? (
        <>
          <p class="hint">{t.enrollBody}</p>
          <ol class="login-steps">
            <li>{t.enroll1}</li>
            <li>
              {t.enroll2}
              {setup ? (
                <>
                  <img class="login-qr" src={setup.qr} alt={t.qr} width={180} height={180} />
                  <span class="hint">{t.enroll2b}</span> <code class="login-secret">{setup.secret}</code>
                </>
              ) : !err ? (
                <span class="hint"> {t.loading}</span>
              ) : null}
            </li>
            <li>{t.enroll3}</li>
          </ol>
        </>
      ) : (
        <p class="hint">{t.challengeBody}</p>
      )}
      <div class="login-field">
        <label class="label" for="mfa-code">
          {t.code}
        </label>
        <input id="mfa-code" class="input portal-code" inputMode="numeric" autocomplete="one-time-code" maxLength={7} pattern="[0-9 ]*" value={code} required onInput={(e) => setCode((e.target as HTMLInputElement).value)} />
      </div>
      <Err text={err} />
      <button type="submit" class="btn btn-primary login-submit" disabled={busy || !factor}>
        {busy ? t.signingIn : t.confirm}
      </button>
      {p.onCancel ? (
        <button type="button" class="btn-link login-link" onClick={p.onCancel}>
          {t.back}
        </button>
      ) : null}
    </form>
  );
}

/** After the password (sign-in or a new one): straight in, or two-step first. */
function AfterPassword(p: { r: SignInResult; onCancel?: () => void }) {
  const t = TX[useLang()];
  useEffect(() => {
    if (p.r.mfa === 'none') finish(p.r.session);
  }, [p.r]);
  if (p.r.mfa === 'none') return <p role="status">{t.signingIn}</p>;
  return <MfaStep token={p.r.session.accessToken} mode={p.r.mfa} factorId={p.r.mfa === 'challenge' ? p.r.factorId : undefined} onDone={finish} onCancel={p.onCancel} />;
}

function Forgot(p: { email: string; onBack: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [email, setEmail] = useState(p.email);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await anon().post('/api/auth/forgot', { email, lang });
      setSent(true);
    } catch (x) {
      setErr(errText(x));
    }
    setBusy(false);
  };
  return (
    <form class="login-form" onSubmit={(e) => void submit(e)}>
      <h2 class="login-step">{t.forgotTitle}</h2>
      {sent ? (
        <p role="status">{t.sent(email.trim())}</p>
      ) : (
        <>
          <p class="hint">{t.forgotBody}</p>
          <div class="login-field">
            <label class="label" for="fg-email">
              {t.email}
            </label>
            <input id="fg-email" class="input input--wide" type="email" autocomplete="username" value={email} required onInput={(e) => setEmail((e.target as HTMLInputElement).value)} />
          </div>
          <Err text={err} />
          <button type="submit" class="btn btn-primary login-submit" disabled={busy}>
            {t.send}
          </button>
        </>
      )}
      <button type="button" class="btn-link login-link" onClick={p.onBack}>
        {t.back}
      </button>
    </form>
  );
}

/**
 * The sign-in card's password; `onAway` while it shows another step (two-step, forgot). With `email` the address was
 * already given on the page before (one sign-in page, owner 2026-10-31) and only the password is asked; `secondary`
 * when Google is the main way (a KFF address) and the password is the fallback under it.
 */
export function PasswordSignin(p: { onAway?: (away: boolean) => void; email?: string; secondary?: boolean } = {}) {
  const lang = useLang();
  const t = TX[lang];
  const [email, setEmail] = useState(p.email ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [step, setStep] = useState<'form' | 'forgot' | SignInResult>('form');
  useEffect(() => p.onAway?.(step !== 'form'), [step]);
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      setStep(await anon().post<SignInResult>('/api/auth/password', { email, password, lang }));
    } catch (x) {
      setErr(errText(x));
      setBusy(false);
    }
  };
  const back = () => {
    setStep('form');
    setBusy(false);
    setPassword('');
  };
  if (step === 'forgot') return <Forgot email={email} onBack={back} />;
  if (step !== 'form') return <AfterPassword r={step} onCancel={back} />;
  return (
    <form class="login-form" onSubmit={(e) => void submit(e)}>
      {!p.email || p.secondary ? (
        <p class="login-or">
          <span>{t.or}</span>
        </p>
      ) : null}
      {p.email ? (
        // the address given before: kept for the browser's password manager
        <input type="email" autocomplete="username" value={email} hidden readOnly />
      ) : (
        <div class="login-field">
          <label class="label" for="pw-email">
            {t.email}
          </label>
          <input id="pw-email" class="input input--wide" type="email" autocomplete="username" value={email} required onInput={(e) => setEmail((e.target as HTMLInputElement).value)} />
        </div>
      )}
      <PasswordInput id="pw-password" label={t.password} value={password} onInput={setPassword} autocomplete="current-password" />
      <Err text={err} />
      <button type="submit" class={p.secondary ? 'btn login-submit' : 'btn btn-primary login-submit'} disabled={busy}>
        {busy ? t.signingIn : p.secondary ? t.signInPw : t.signIn}
      </button>
      <button type="button" class="btn-link login-link" onClick={() => setStep('forgot')}>
        {t.forgot}
      </button>
    </form>
  );
}

/** /auth/password#t=…: the invite or "Forgot password" link. Choose a password, then signed in. */
export function SetPasswordPage(p: { lang: Lang }) {
  const t = TX[p.lang];
  const [token] = useState(() => new URLSearchParams(location.hash.slice(1)).get('t') ?? '');
  const [info, setInfo] = useState<{ email: string; name: string; purpose: 'invite' | 'reset' } | null>(null);
  const [gone, setGone] = useState(!token);
  const [pw, setPw] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [after, setAfter] = useState<SignInResult | null>(null);
  useEffect(() => {
    // the token leaves the address bar at once
    if (location.hash) history.replaceState(null, '', '/auth/password');
    if (token) anon().post<typeof info>('/api/auth/link', { token }).then(setInfo, () => setGone(true));
  }, []);
  const submit = async (e: Event) => {
    e.preventDefault();
    if (pw !== again) return setErr(t.mismatch);
    setBusy(true);
    setErr('');
    try {
      setAfter(await anon().post<SignInResult>('/api/auth/set-password', { token, password: pw, lang: p.lang }));
    } catch (x) {
      if (x instanceof ApiError && x.status === 410) setGone(true);
      setErr(errText(x));
      setBusy(false);
    }
  };
  return (
    <LangContext.Provider value={p.lang}>
      <div class="login login--single" lang={p.lang}>
        <main class="login-main">
          <section class="login-card" aria-labelledby="sp-title">
            <span class="login-brand login-brand--card">
              <span class="brand-mark" aria-hidden="true">
                KFF
              </span>
              <span aria-hidden="true">Hub</span>
              <span class="sr-only">KFF Hub</span>
            </span>
            {after ? (
              <AfterPassword r={after} />
            ) : gone ? (
              <>
                <h1 id="sp-title">{t.linkGone}</h1>
                <a class="btn" href="/">
                  {t.newLink}
                </a>
              </>
            ) : !info ? (
              <p>{t.loading}</p>
            ) : (
              <form class="login-form" onSubmit={(e) => void submit(e)}>
                <h1 id="sp-title">{info.purpose === 'invite' ? t.setTitle(info.name) : t.resetTitle}</h1>
                <p class="hint">
                  {t.username} <b>{info.email}</b>
                </p>
                {/* the username field lets the browser's password manager save the pair */}
                <input type="email" autocomplete="username" value={info.email} hidden readOnly />
                <PasswordInput id="sp-new" label={t.newPassword} value={pw} onInput={setPw} autocomplete="new-password" describedBy="sp-rules" />
                <p id="sp-rules" class="hint">
                  {t.rules}
                </p>
                <PasswordInput id="sp-again" label={t.repeat} value={again} onInput={setAgain} autocomplete="new-password" />
                <Err text={err} />
                <button type="submit" class="btn btn-primary login-submit" disabled={busy}>
                  {t.save}
                </button>
              </form>
            )}
          </section>
        </main>
      </div>
    </LangContext.Provider>
  );
}
